import { GetServerSidePropsContext, NextPage } from 'next'
import QueryString from 'qs'

import {
  GetServerSidePropsReturnType,
  PageProps,
} from '../../relay/Common/PageProps'
import getConfiguration from '../../rest/services/getConfiguration'
import RestApiClient from '../../rest/client'
import { initWithLangAndLabels } from '../../i18n'
import ProductsTemplate from '../../templates/ProductsTemplate'
import categoryBySlug from '../../rest/requests/categories/categoryBySlug'
import { Category } from '../../rest/types/Category'
import { Offers } from '../../rest/types/Offer'
import getBrands from '../../rest/requests/brands/getBrands'
import { Brands } from '../../rest/types/Brand'
import getOffersWithTotal from '../../rest/requests/offers/getOffersWithTotal'
import useOfferList from '../../relay/Offer/useOfferList'
import { ProductsFiltersFormValues } from '../../components/ProductsFilters'
import { OFFERS_LIST_LIMIT } from '../../constant'
import { SeoTransformer } from '../../relay/Transformers/SeoTransformer'
import Router from '../../routes/Router'
import routes from '../../routes/definitions'

type Props = PageProps & {
  category: Category
  offers: Offers
  brands: Brands
  total: number
  filters: ProductsFiltersFormValues
}

const CategoryPage: NextPage<Props> = ({
  category,
  total,
  offers,
  brands,
  configuration,
  filters,
}) => {
  const props = useOfferList({
    offers,
    total,
    configuration,
    brands,
    category,
    filters,
  })

  return <ProductsTemplate {...props} />
}

export default CategoryPage

export async function getServerSideProps(
  context: GetServerSidePropsContext
): GetServerSidePropsReturnType<Props> {
  const slug: string = String(context?.query?.slug ?? '')
  const filtersParams: any = context?.query?.filters
    ? QueryString.parse(context?.query?.filters as string, {
        delimiter: ',',
      })
    : null

  const limit = context?.query?.limit ? (context?.query?.limit as string) : null

  const [configuration, categoryResult, brandsResults] = await Promise.all([
    getConfiguration(),
    RestApiClient.call(categoryBySlug({ slug })),
    RestApiClient.call(getBrands()),
  ])

  const category = categoryResult?.data as any
  const brands = brandsResults?.data as any
  const i18n = initWithLangAndLabels('fr', configuration.translations)

  context.res.setHeader(
    'Cache-Control',
    'public, s-maxage=10, stale-while-revalidate=59'
  )

  if (!category?.id) {
    return {
      notFound: true,
      props: {
        i18n,
        configuration,
      },
    }
  }

  const [offersResult] = await Promise.all([
    RestApiClient.call(
      getOffersWithTotal({
        filters: {
          categories: { documentId: { $in: [category.id] } },
          ...(filtersParams && {
            ...filtersParams?.filters,
          }),
        },
        limit: limit || OFFERS_LIST_LIMIT,
      })
    ),
  ])

  const offers = offersResult?.data?.offers ?? []
  const total = offersResult?.data?.total ?? 0

  return {
    props: {
      category,
      metas: SeoTransformer(
        Router.getRouteUrl(routes.category, context?.params),
        category?.seo
      ),
      total,
      offers,
      brands,
      configuration,
      filters: filtersParams?.filters || null,
      i18n,
    },
  }
}
